import { Button, Divider, Select, Space, Tooltip, Upload } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  HighlightOutlined,
  CodeOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  PictureOutlined,
  LinkOutlined,
  UndoOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { api } from '../../api/client';

/** 依 Tiptap 官方擴充建的完整工具列。 */
export default function Toolbar({ editor }: { editor: Editor }) {
  // 訂閱 editor 狀態，按鈕 active 會即時更新
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      highlight: e.isActive('highlight'),
      code: e.isActive('code'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      link: e.isActive('link'),
      block: e.isActive('heading', { level: 1 })
        ? 'h1'
        : e.isActive('heading', { level: 2 })
          ? 'h2'
          : e.isActive('heading', { level: 3 })
            ? 'h3'
            : 'p',
    }),
  });

  const c = () => editor.chain().focus();
  const tb = (active: boolean, icon: React.ReactNode, on: () => void, tip: string) => (
    <Tooltip title={tip}>
      <Button size="small" type={active ? 'primary' : 'text'} icon={icon} onClick={on} />
    </Tooltip>
  );

  const setBlock = (v: string) => {
    if (v === 'p') c().setParagraph().run();
    else c().toggleHeading({ level: Number(v[1]) as 1 | 2 | 3 }).run();
  };
  const addLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('連結網址', prev ?? 'https://');
    if (url === null) return;
    if (url === '') c().unsetLink().run();
    else c().toggleLink({ href: url }).run();
  };
  const upload = async (file: File) => {
    const { url } = await api.uploadImage(file);
    c().setImage({ src: url }).run();
    return false;
  };

  return (
    <Space wrap size={2} className="border-b border-gray-200 bg-black/[0.02] p-2">
      {tb(false, <UndoOutlined />, () => c().undo().run(), '復原')}
      {tb(false, <RedoOutlined />, () => c().redo().run(), '重做')}
      <Divider type="vertical" />
      <Select
        size="small"
        value={s.block}
        className="w-[92px]"
        onChange={setBlock}
        options={[
          { value: 'p', label: '內文' },
          { value: 'h1', label: '標題 1' },
          { value: 'h2', label: '標題 2' },
          { value: 'h3', label: '標題 3' },
        ]}
      />
      <Divider type="vertical" />
      {tb(s.bold, <BoldOutlined />, () => c().toggleBold().run(), '粗體')}
      {tb(s.italic, <ItalicOutlined />, () => c().toggleItalic().run(), '斜體')}
      {tb(s.underline, <UnderlineOutlined />, () => c().toggleUnderline().run(), '底線')}
      {tb(s.strike, <StrikethroughOutlined />, () => c().toggleStrike().run(), '刪除線')}
      {tb(s.highlight, <HighlightOutlined />, () => c().toggleHighlight().run(), '螢光標記')}
      {tb(s.code, <CodeOutlined />, () => c().toggleCode().run(), '行內碼')}
      <Divider type="vertical" />
      {tb(s.bullet, <UnorderedListOutlined />, () => c().toggleBulletList().run(), '項目清單')}
      {tb(s.ordered, <OrderedListOutlined />, () => c().toggleOrderedList().run(), '編號清單')}
      {tb(s.quote, '“”', () => c().toggleBlockquote().run(), '引言')}
      <Divider type="vertical" />
      {tb(false, <AlignLeftOutlined />, () => c().setTextAlign('left').run(), '靠左')}
      {tb(false, <AlignCenterOutlined />, () => c().setTextAlign('center').run(), '置中')}
      {tb(false, <AlignRightOutlined />, () => c().setTextAlign('right').run(), '靠右')}
      <Divider type="vertical" />
      {tb(s.link, <LinkOutlined />, addLink, '連結')}
      <Upload showUploadList={false} beforeUpload={upload} accept="image/*">
        <Tooltip title="插入圖片">
          <Button size="small" type="text" icon={<PictureOutlined />} />
        </Tooltip>
      </Upload>
      {tb(false, '—', () => c().setHorizontalRule().run(), '水平線')}
    </Space>
  );
}
